/**
 * MessageInputView: ご要望・メッセージ（message）／ご訪問希望場所（visitPreferredLocation）入力欄。
 * 2026-08-04対面商談対応（U12）BR-U6-12。design-mockups/pre-booking-form.html Case4/5準拠。
 *
 * `message`は単一カラムでラベル・プレースホルダーのみmeetingTypeで分岐する
 * （ONLINE=「目的・問い合わせ内容」／ON_SITE=「ご要望・メッセージ」）。
 * `visitPreferredLocation`はON_SITE限定の入力欄で、ONLINE切替時は非表示にするが
 * 値はメモリ上に保持し、ON_SITEに戻したら復元する。
 */

import { createElement, setText } from "../core/shadowDomUtil";
import type { MeetingType } from "../types";

export interface MessageInputValues {
  message: string;
  /** meetingType=ONLINEの場合は常にundefined（送信しない）。 */
  visitPreferredLocation?: string;
}

export interface MessageInputResult {
  element: HTMLElement;
  getValues: () => MessageInputValues;
  /** 商談形式が変わった際に呼び出し元から呼ぶ。message値は保持し、表示内容のみ切り替える。 */
  updateMeetingType: (meetingType: MeetingType) => void;
}

const MESSAGE_LABEL: Record<MeetingType, string> = {
  ONLINE: "目的・問い合わせ内容（任意）",
  ON_SITE: "ご要望・メッセージ（任意）",
};

const MESSAGE_PLACEHOLDER: Record<MeetingType, string> = {
  ONLINE: "相談したい内容をご記入ください",
  ON_SITE: "相談したい内容をご記入ください",
};

const VISIT_PREFERRED_LOCATION_PLACEHOLDER = "例：自社オフィス（東京都千代田区）／〇〇工場 など";

/**
 * message/visitPreferredLocation入力欄を構築する。
 *
 * @param initialMeetingType 初期表示時のmeetingType（HYBRID時はMeetingTypeSelectionViewの既定値=ONLINEに合わせる）
 */
export function renderMessageInput(initialMeetingType: MeetingType): MessageInputResult {
  const wrapper = createElement("div");

  // ご訪問希望場所（ON_SITE限定）。ONLINE時はDOMから外し、値はメモリに保持する。
  let visitPreferredLocationValue = "";
  const visitPreferredLocationLabel = createElement("p", {
    className: "spica-input-label",
    text: "ご訪問希望場所（任意）",
  });
  const visitPreferredLocationInput = createElement("input", {
    className: "spica-input-field",
    attrs: { type: "text", placeholder: VISIT_PREFERRED_LOCATION_PLACEHOLDER, maxlength: "2000" },
  }) as HTMLInputElement;
  visitPreferredLocationInput.addEventListener("input", () => {
    visitPreferredLocationValue = visitPreferredLocationInput.value;
  });

  const messageLabel = createElement("p", { className: "spica-input-label" });
  const messageTextarea = createElement("textarea", {
    className: "spica-input-field",
    attrs: { rows: "2", placeholder: MESSAGE_PLACEHOLDER[initialMeetingType], maxlength: "2000" },
  }) as HTMLTextAreaElement;

  const applyLabels = (meetingType: MeetingType): void => {
    setText(messageLabel, MESSAGE_LABEL[meetingType]);
    messageTextarea.placeholder = MESSAGE_PLACEHOLDER[meetingType];
  };

  const applyVisitPreferredLocationVisibility = (meetingType: MeetingType): void => {
    if (meetingType === "ON_SITE") {
      // モック準拠（ebook-viewing-tag-proposal.html等）: ご要望・メッセージ欄の「後ろ」に
      // ご訪問希望場所欄を配置する。messageTextareaの直後に挿入する（末尾追加）。
      wrapper.appendChild(visitPreferredLocationLabel);
      wrapper.appendChild(visitPreferredLocationInput);
      visitPreferredLocationInput.value = visitPreferredLocationValue;
    } else {
      if (visitPreferredLocationLabel.parentElement === wrapper) wrapper.removeChild(visitPreferredLocationLabel);
      if (visitPreferredLocationInput.parentElement === wrapper) wrapper.removeChild(visitPreferredLocationInput);
    }
  };

  applyLabels(initialMeetingType);
  wrapper.appendChild(messageLabel);
  wrapper.appendChild(messageTextarea);
  applyVisitPreferredLocationVisibility(initialMeetingType);

  return {
    element: wrapper,
    getValues(): MessageInputValues {
      const message = messageTextarea.value;
      if (visitPreferredLocationLabel.parentElement === wrapper) {
        return { message, visitPreferredLocation: visitPreferredLocationValue };
      }
      return { message };
    },
    updateMeetingType(meetingType: MeetingType): void {
      applyLabels(meetingType);
      applyVisitPreferredLocationVisibility(meetingType);
    },
  };
}
